import { useEffect, useState, useMemo, useCallback } from 'react';

import { GraphQLClient } from 'graphql-request';

import { QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'motion/react';

import { AppContext } from '../app.context';
import { useDb, useInstalled, useResize } from '../app.hooks';
import config from '@app/config.window';

import {
	useColorScheme,
	useDeviceInformation,
	useDocumentTitle,
	usePrintStatus,
	useDialog,
	useIsOnline,
	usePersistentStorage,
	useMotionPreference,
	useReducedMotion,
	type MotionPreference,
} from '@shared/lib/hooks';

import { Password, usePassword } from '@pages/password';
import { getGraphQLClient } from '@shared/auth';
import { PSEUDO_LANGUAGE, setLanguage, useLanguage } from '@shared/i18n';

import { About } from '@widgets/about';
import { OfflineNotice } from '@shared/ui/offline-notice';
import { Toaster } from '@shared/ui/sonner';

import {
	APP_KEY_STORAGE_KEY,
	LocalStorageKV,
	clearDisposableLocalStorage,
} from '@shared/storage';
import {
	defaultQueryClient,
	bootQueryPersistence,
	setCacheDurationsForConnectivity,
} from '@shared/query-client';
import { setUnauthenticatedHandler } from '@shared/api';

// Lets services/api/client.ts invalidate the cache on a 401 without importing
// the QueryClient itself (which would rebuild the services -> query-client ->
// views/templates import cycle). Module scope, not an effect: a 401 can land
// before any effect has run.
setUnauthenticatedHandler(() => defaultQueryClient.invalidateQueries());

import { ALERT_TIMEOUT_KEYS } from '@/constants';
import {
	InstallationDialog,
	useInstallDialog,
	useInstallPrompt,
} from '@features/install-pwa';
import { Analytics } from '@shared/analytics';
import { loadAppId, setOffline } from '@shared/events';
import { ErrorBoundary, ErrorFallback } from '@shared/ui/error-boundary';
import { DbOpenError } from '@shared/ui/db-open-error';
import { reportClientError } from '@shared/observability';
import Template from '@/templates/template';
import { ServiceWorkerProvider } from '@shared/sw';
import { useBuildVersionCheck } from '@shared/sw';
import { AppRendererLabels } from './renderer-labels/renderer-labels';

import './app.css';

const MOTION_CONFIG_REDUCED_MOTION = {
	system: 'user',
	reduce: 'always',
	full: 'never',
} as const satisfies Record<MotionPreference, 'user' | 'always' | 'never'>;

declare global {
	interface Window {
		onbeforeinstallprompt?: unknown;
	}
}

function App() {
	const isOnline = useIsOnline();
	const {
		colorScheme,
		preference: colorSchemePreference,
		setColorSchemePreference,
	} = useColorScheme();
	const { contentProtection } = config.app;
	const device = useDeviceInformation();
	const { appKey, languages, title } = config.build;

	const [graphQLClient, setGraphQLClient] =
		useState<GraphQLClient>(getGraphQLClient());

	// defaultQueryClient is a module-level singleton (see QueryClient.ts) --
	// no state wrapper needed, unlike the old useState<QueryClient>(...) plus
	// an effect that rewrote networkMode on every useIsOnline() change and
	// called setQueryClient with the *same* reference (a no-op re-render,
	// since QueryClient mutates in place). networkMode is a static default
	// now; gcTime and the persister's maxAge are the two settings still
	// intentionally connectivity-dependent (2h online / 24h offline, a
	// resource-management decision, not a correctness one -- see
	// setCacheDurationsForConnectivity's own comment), so they're still
	// updated reactively below, but narrowly: only those two fields change,
	// and nothing forces a re-render to do it.
	const queryClient = defaultQueryClient;

	useBoot();
	useBuildVersionCheck();

	// Ordered before bootQueryPersistence's effect (declaration order is
	// execution order for effects in the same component) so the very first
	// restore/gc pass already uses the right maxAge instead of the module's
	// safe-default one.
	useEffect(() => {
		setCacheDurationsForConnectivity(!!isOnline);
		setOffline(!isOnline);
		if (isOnline) {
			// No-op once the device has the id; otherwise retried whenever
			// the app comes back online.
			void loadAppId();
		}
	}, [isOnline]);

	useEffect(() => {
		bootQueryPersistence(queryClient);
	}, [queryClient]);

	const kv = useMemo(() => new LocalStorageKV('cf-kv'), []);

	const { isSupportInstallPrompt, deferredPrompt } = useInstallPrompt();

	const [contentCSS, setContentCSS] = useState('');

	const isInstalled = useInstalled();
	usePersistentStorage(isInstalled);
	const { shouldDisplayPassword, onSuccessPassword } = usePassword();
	const { show: showDialog, setShowDialog } = useDialog();
	const { show: showInstallDialog, setShowInstallation } = useInstallDialog();

	const isTouchable = useMemo(
		() => window.matchMedia('(pointer: coarse)').matches,
		[],
	);

	// Live, unlike isTouchable above: the reader's Settings choice, or the OS
	// setting (which they can toggle while the app is open) when that choice
	// is "system". Exposed on AppContext as prefersReducedMotion.
	const { motionPreference } = useMotionPreference();
	const prefersReducedMotion = useReducedMotion();

	// Keeps `<html data-motion>` in step for the CSS; index-template.ejs sets
	// it before first paint with the same rule (see useReducedMotion).
	useEffect(() => {
		document.documentElement.setAttribute(
			'data-motion',
			prefersReducedMotion ? 'reduce' : 'full',
		);
	}, [prefersReducedMotion]);

	useEffect(() => {
		localStorage.setItem('app-mode', `${import.meta.env.MODE}`);

		if (contentProtection) {
			document.body.oncontextmenu = (e) => {
				e.preventDefault();
				return false;
			};
			setContentCSS(`@media print {
				html, body {
					   display: none;  /* hide whole page */
				}
			}`);
		}

		// Clear timeouts
		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.ID)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.ID);
		}

		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.DATETIME)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.DATETIME);
		}
	}, [contentProtection]);
	useResize();
	useDocumentTitle(title);

	const isPrinting = usePrintStatus();
	const { db, error: dbError } = useDb();

	// The app language (Settings → General → Languages): UI labels and
	// content both follow it. Owned by @shared/i18n's store, which persists
	// the choice, loads its catalog, and keeps `<html lang dir>` in sync.
	// The dev-only pseudo-locale only relabels the UI; content stays English.
	const uiLanguage = useLanguage();
	const lang =
		uiLanguage === PSEUDO_LANGUAGE ? config.build.language : uiLanguage;
	const onLanguageChange = useCallback((language: string) => {
		void setLanguage(language);
	}, []);

	const rebuildGraphQLClient = useCallback(() => {
		setGraphQLClient(getGraphQLClient());
	}, []);

	const parseGraphQLClientError = useCallback((err: unknown) => {
		try {
			const reqRes = JSON.parse(JSON.stringify(err));
			return new Error(reqRes.response.errors[0].message);
		} catch {
			throw err;
		}
	}, []);

	const contextValue = useMemo(
		() => ({
			db: db ?? undefined,
			appKey,
			graphQLClient,
			baseUrl: config?.build?.subdirectory ?? '/',
			lang: lang ?? 'en',
			languages,
			onLanguageChange,
			onlineStatus: !!isOnline,
			isPrinting,
			isInstalled,
			device,
			isTouchable,
			prefersReducedMotion,
			isSupportInstallPrompt,
			deferredPrompt,
			kv,
			rebuildGraphQLClient,
			parseGraphQLClientError,
			colorScheme,
			colorSchemePreference,
			setColorSchemePreference,
		}),
		[
			db,
			appKey,
			device,
			graphQLClient,
			lang,
			languages,
			onLanguageChange,
			isOnline,
			isPrinting,
			isInstalled,
			isTouchable,
			prefersReducedMotion,
			isSupportInstallPrompt,
			deferredPrompt,
			kv,
			rebuildGraphQLClient,
			parseGraphQLClientError,
			colorScheme,
			colorSchemePreference,
			setColorSchemePreference,
		],
	);

	// Checked before the loading-state null below: a real failure and
	// "still opening" must not collapse into the same eternal-null render
	// -- that was the bug (a failed Dexie open never rendered anything,
	// and the failure was only ever reported to cf-observability, never
	// shown). See DbOpenError's own comment for the full reasoning.
	if (dbError) {
		return <DbOpenError />;
	}

	if (isOnline === null || !db || !kv) {
		return null;
	}

	if (shouldDisplayPassword) {
		return <Password onSuccess={onSuccessPassword} />;
	}

	return (
		<ServiceWorkerProvider>
			<AppContext.Provider value={contextValue}>
				<QueryClientProvider client={queryClient}>
					{/* One switch for every `motion` animation in the app:
					    with reduced motion on, transform and layout
					    animations are skipped (opacity still fades). "user"
					    defers to the OS; the reader's Settings choice
					    overrides it either way. Here rather than per
					    component, so no call site can forget it. Swiper and
					    animate.css don't read this -- non-`motion` code reads
					    `prefersReducedMotion` from AppContext instead. */}
					<MotionConfig
						reducedMotion={MOTION_CONFIG_REDUCED_MOTION[motionPreference]}
					>
						{/* Last line of defence. The router already gives every
						    route a `defaultErrorComponent` (app/routing/
						    route-boundaries.tsx, wired in createTenantApp), but
						    that only catches throws inside a route component --
						    anything raised by Analytics, by a template's own shell
						    above the router, or during the render that mounts the
						    router itself would still white-screen. Placed inside
						    the providers so the fallback can use the tenant's
						    theme tokens, and keyed to nothing: recovery here is
						    an explicit user action, not automatic. */}
						<ErrorBoundary
							// i18n-ignore: an error-reporting id, not a label
							label="app-root"
							onError={(error, errorInfo) =>
								reportClientError({
									source: 'boundary',
									category: 'app-root',
									error,
									componentStack:
										errorInfo.componentStack ?? undefined,
								})
							}
							fallback={(_error, reset) => (
								<ErrorFallback reset={reset} scope="app" />
							)}
						>
							<Analytics />
							<AppRendererLabels>
								<Template />
							</AppRendererLabels>
						</ErrorBoundary>

						{/* Outside <Template />, so all ten templates share one
						    toast host and one offline notice by construction,
						    rather than each template remembering to render its
						    own. Every `toast(...)` call anywhere in the app --
						    this file's included -- renders into this <Toaster />. */}
						<Toaster />
						<OfflineNotice isOnline={!!isOnline} />

						{contentCSS ? <style>{contentCSS}</style> : null}
						<About
							show={showDialog}
							setShow={(v: boolean) => {
								setShowDialog(v);
							}}
						/>
						{/* Mounted once here, like `<About/>` above, rather
						    than per-template home page -- `AppMenu`'s Install
						    entry and the issue reader's `IssueButton.Install`
						    (`useInstallAction`, `@features/install-pwa`) both
						    toggle this same singleton, so the dialog needs to
						    exist regardless of which page is showing. */}
						<InstallationDialog
							show={showInstallDialog}
							setShowInstallation={setShowInstallation}
						/>
					</MotionConfig>
				</QueryClientProvider>
			</AppContext.Provider>
		</ServiceWorkerProvider>
	);
}

function useBoot() {
	const setBuildValues = () => {
		localStorage.setItem(APP_KEY_STORAGE_KEY, config.build.appKey);
		localStorage.setItem('version', config.build.version);
	};
	useEffect(() => {
		const version = localStorage.getItem('version');
		const appKey = localStorage.getItem(APP_KEY_STORAGE_KEY);
		if (!appKey || !version) {
			clearDisposableLocalStorage();
			setBuildValues();
			return;
		}

		if (
			appKey !== config.build.appKey ||
			version !== config.build.version
		) {
			clearDisposableLocalStorage();
			setBuildValues();
			return;
		}
	}, []);
}

export default App;
